import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/ui/Header';
import Footer from '../components/ui/Footer';
import { Mail, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen relative z-[1]">
      <Header />

      {/* Content */}
      <div className="pt-32 pb-20">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-12">
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50 mb-4">Legal</Badge>
            <h1 className="text-5xl md:text-6xl font-black text-white mb-4">
              Terms of <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Service</span>
            </h1>
            <p className="text-slate-400 text-lg">Last Updated: June 29, 2025</p>
          </div>

          <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-8 md:p-12 space-y-8 text-slate-300 leading-relaxed">
            <div>
              <p>
                Welcome to PHOS Studio! These Terms of Service ("Terms") govern your use of our website and services. By accessing or using our services, you agree to comply with and be bound by these Terms. If you do not agree, please do not use our services.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">1. Who We Are</h2>
              <ul className="space-y-2">
                <li><strong className="text-white">PHOS Management Inc.</strong> ("PHOS Studio," "we," "us," or "our")</li>
                <li>12200 Sibley Road, Riverview, MI 48193</li>
                <li>Website: phosstudio.net</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">2. Use of Our Services</h2>
              <p className="mb-3">When you use our website or services, you agree to:</p>
              <ul className="space-y-2 list-disc list-inside">
                <li>Follow all applicable laws and regulations.</li>
                <li>Provide accurate information when creating an account or contacting us.</li>
                <li>Not use our services for any illegal or unauthorized purpose.</li>
                <li>Not attempt to disrupt or damage our website, servers, or networks.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">3. Accounts</h2>
              <p className="mb-3">If you create an account:</p>
              <ul className="space-y-2 list-disc list-inside">
                <li>You are responsible for maintaining the security of your login credentials.</li>
                <li>You are responsible for all activity that occurs under your account.</li>
                <li>You must notify us immediately of any unauthorized use of your account.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">4. Intellectual Property</h2>
              <p>
                All content on our website, including videos, images, logos, and text, is the property of PHOS Studio unless otherwise stated. You may not copy, reproduce, or distribute our content without our written permission.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">5. Payments and Services</h2>
              <p className="mb-3">When you purchase services from us:</p>
              <ul className="space-y-2 list-disc list-inside">
                <li>You agree to pay all fees as described at the time of purchase.</li>
                <li>All payments are due in advance unless otherwise stated.</li>
                <li>Subscription-based services will renew automatically unless canceled in accordance with our cancellation policy.</li>
                <li>We reserve the right to change prices at any time, with notice provided in advance.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">6. Cancellation & Refunds</h2>
              
              <h3 className="text-xl font-bold text-amber-400 mb-3">For subscription or retainer services:</h3>
              <ul className="space-y-2 list-disc list-inside mb-4">
                <li>Cancellations must be submitted in writing at least 15 days before the next billing cycle.</li>
                <li>Refunds are only issued at our sole discretion and based on the terms of your service agreement.</li>
              </ul>

              <h3 className="text-xl font-bold text-amber-400 mb-3">For one-time project services:</h3>
              <ul className="space-y-2 list-disc list-inside">
                <li>Deposits are non-refundable once work has commenced.</li>
                <li>Final payment is due upon project completion unless otherwise stated.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">7. Third-Party Links</h2>
              <p>
                Our services may contain links to third-party websites. We do not control or endorse these sites and are not responsible for their content or practices. Use them at your own risk.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">8. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, PHOS Studio is not liable for any indirect, incidental, special, or consequential damages arising out of your use of our services. This includes but is not limited to loss of data, revenue, or business opportunities.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">9. Termination</h2>
              <p>
                We reserve the right to suspend or terminate your access to our services at any time, without prior notice, if you violate these Terms or misuse our services.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">10. Changes to These Terms</h2>
              <p>
                We may update these Terms from time to time. When we do, we will revise the "Last Updated" date above. Continued use of our services after changes take effect constitutes your acceptance of the updated Terms.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">11. Governing Law</h2>
              <p>
                These Terms are governed by the laws of the State of Michigan, United States, without regard to its conflict of law principles.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">12. Contact Us</h2>
              <p className="mb-4">For questions about these Terms, please contact us:</p>
              <div className="space-y-3 bg-slate-800/50 p-6 rounded-lg border border-amber-500/20">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-amber-400" />
                  <a href="mailto:contact@phosstudio.net" className="text-amber-400 hover:text-amber-300 transition-colors">
                    contact@phosstudio.net
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-amber-400" />
                  <a href="tel:+13133626984" className="text-amber-400 hover:text-amber-300 transition-colors">
                    +1 313-362-6984
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link to="/">
              <Button className="bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-950 hover:from-amber-400 hover:to-yellow-300 font-bold">
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </div> 
      <Footer />
    </div>
  );
};

export default TermsOfService;
